import { IconName } from '@purpur/library/icon';

import { NavigationItem } from '@telialt/models/src/Navigation';
import { contentfulImageUrl } from '@telialt/utils/src/Contentful/contentfulImageUrl';

import { CtaData, ResponsiveImageGroup } from '../components/common/model';
import { CtaType, LayoutType, NavigationItemType, ResponsiveImageType } from '../models/Content';
import { LayoutFooterData, LayoutNavigationData } from '../models/Data';

const NAVIGATION_ITEM_TRANSFORM_MAX_DEPTH = 10;

const transformNavigationItem = (item: NavigationItemType, depth = 0): NavigationItem => {
    return {
        title: item.fields.title,
        url: item.fields.url,
        openInNewTab: item.fields.openInANewTab,
        icon: item.fields.icon as IconName,
        colorOption: item.fields.colorOption,
        displayOption: item.fields.displayOption,
        badge: item.fields.badge,
        component: item.fields.component,
        noFollow: item.fields.noFollow,
        children:
            depth < NAVIGATION_ITEM_TRANSFORM_MAX_DEPTH - 1
                ? item.fields.children?.map((child) => transformNavigationItem(child, depth + 1))
                : undefined,
    };
};

export const transformNavigationData = (layout?: LayoutType): LayoutNavigationData => ({
    contextLeftMenu: layout.fields.navigationContextLeftMenu?.map(transformNavigationItem),
    contextRightMenu: layout.fields.navigationContextRightMenu?.map(transformNavigationItem),
    mainLeftMenu: layout.fields.navigationMainLeftMenu?.map(transformNavigationItem),
    mainRightMenu: layout.fields.navigationMainRightMenu?.map(transformNavigationItem),
    additionalMenu: layout.fields.navigationAdditionalMenu?.map(transformNavigationItem),
});

export const transformFooterData = (layout?: LayoutType): LayoutFooterData => ({
    socialMenu: layout.fields.footerSocialMenu?.map(transformNavigationItem),
    mainMenu: layout.fields.footerMainMenu?.map(transformNavigationItem),
    additionalMenu: layout.fields.footerAdditionalMenu?.map(transformNavigationItem),
    copyrightText: layout.fields.footerCopyrightText,
});

export const transformCta = (cta?: CtaType): CtaData | undefined => {
    if (!cta) {
        return undefined;
    }

    return {
        id: cta.sys.id,
        title: cta.fields.title,
        url: cta.fields.url,
        target: cta.fields.openInANewTab ? '_blank' : undefined,
        rel: cta.fields.nofollow ? 'nofollow' : undefined,
        image: cta.fields.image?.fields?.file?.url
            ? {
                  src: contentfulImageUrl(cta.fields.image.fields.file.url),
                  alt: cta.fields.image.fields?.description || '',
                  width: cta.fields.image.fields.file.details?.image?.width,
                  height: cta.fields.image.fields.file.details?.image?.height,
              }
            : undefined,
        referencedContent: cta.fields.referencedContent,
    };
};

export const transformResponsiveImage = (images?: ResponsiveImageType): ResponsiveImageGroup | undefined => {
    if (
        !images ||
        !images.fields.imageSm?.fields?.file?.url ||
        !images.fields.imageMd?.fields?.file?.url ||
        !images.fields.imageLg?.fields?.file?.url
    ) {
        return undefined;
    }

    return {
        imageSm: {
            src: contentfulImageUrl(images.fields.imageSm.fields.file.url),
            alt: images.fields.imageSm.fields.description,
            width: images.fields.imageSm.fields.file.details?.image?.width,
            height: images.fields.imageSm.fields.file.details?.image?.height,
        },
        imageMd: {
            src: contentfulImageUrl(images.fields.imageMd.fields.file.url),
            alt: images.fields.imageMd.fields.description,
            width: images.fields.imageMd.fields.file.details?.image?.width,
            height: images.fields.imageMd.fields.file.details?.image?.height,
        },
        imageLg: {
            src: contentfulImageUrl(images.fields.imageLg.fields.file.url),
            alt: images.fields.imageLg.fields.description,
            width: images.fields.imageLg.fields.file.details?.image?.width,
            height: images.fields.imageLg.fields.file.details?.image?.height,
        },
        altText: images.fields.altText,
    };
};
